/**
 * Load Family Tool - BIM 2025 Grade
 *
 * Loads RFA family files or Type Catalog (.txt) files.
 * Supports:
 * - JSON family schema loading (via FamilyLoader)
 * - Category validation
 * - Overwrite behavior (Existing vs New values)
 * Opens a real file picker on activation (BIM: Load Family dialog).
 */

import { BaseTool } from './BaseTool';
import { ThreeEvent } from '@react-three/fiber';
import { familyLoader } from '../core/services';
import { familyManager } from '../core/FamilyManager';
import { FamilySchema } from '../core/families/FamilySchema';
import i18n from '../i18n';

export interface LoadFamilyOptions {
    filePath: string;
    overwrite: 'OverwriteParameterValues' | 'UseExistingValues';
}

export class LoadFamilyTool extends BaseTool {
    id = 'load_family';
    name = 'Load Family';
    cursor = 'default';

    private options: LoadFamilyOptions = {
        filePath: '',
        overwrite: 'OverwriteParameterValues'
    };

    onActivate(): void {
        // Dialog-driven command: BIM opens the family library dialog.
        void this.pickFile('.yapfamily,.json,.rfa').then((file) => {
            if (file) {
                void this.loadFromFile(file);
            } else {
                this.exitToSelect();
            }
        });
    }

    private async loadFromFile(file: File): Promise<void> {
        try {
            const content = await file.text();

            if (file.name.toLowerCase().endsWith('.rfa')) {
                // Binary RFA parsing is not supported in the browser build;
                // register a placeholder family so the load is still tracked.
                const familyName = this.extractFileName(file.name);
                familyManager.registerFamily({
                    name: familyName,
                    category: 'Generic Models',
                    path: file.name,
                    params: {},
                    typeParams: {},
                });
                this.notify(i18n.t('tools.notify.familyRegistered', { name: familyName }), 'warning');
            } else {
                const schema = familyLoader.parseFromString(content);
                if (!schema) {
                    this.notify(i18n.t('tools.notify.familyParseFailed', { fileName: file.name }), 'error');
                    return;
                }
                await familyLoader.loadFamily(schema as FamilySchema);
                this.notify(i18n.t('tools.notify.familyLoaded', { name: schema.name }), 'success');
            }
            this.options.filePath = file.name;
        } catch (err) {
            this.notify(i18n.t('tools.notify.familyLoadFailed', { error: String(err) }), 'error');
        } finally {
            this.exitToSelect();
        }
    }

    /**
     * Load with a known path (kept for tests/macros)
     */
    public executeLoad(filePath: string): boolean {
        this.options.filePath = filePath;

        const familyName = this.extractFileName(filePath);
        familyManager.registerFamily({
            name: familyName,
            category: 'Generic Models',
            path: filePath,
            params: {},
            typeParams: {},
        });
        this.notify(i18n.t('tools.notify.familyRegisteredNamed', { name: familyName }), 'success');
        return true;
    }

    onPointerDown(_event: ThreeEvent<PointerEvent>): void {
        // Not interactive
    }

    onPointerMove(_event: ThreeEvent<PointerEvent>): void {
        // Not used
    }

    onPointerUp(_event: ThreeEvent<PointerEvent>): void {
        // Not used
    }

    private extractFileName(path: string): string {
        const basename = path.split(/[\\/]/).pop() || path;
        return basename.replace(/\.(rfa|RFA)$/, '');
    }
}
